import { inject,Injectable, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
  catchError,
  filter,
  first,
  Observable,
  of,
  retryWhen,
  Subject,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';

import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';

import {
  Account,
  AccountCertificate,
  AccountServiceError,
} from '@personal-area/global/accounts/models';
import {
  AccountService,
  AccountsFacade,
  AccountSocketService,
} from '@personal-area/global/accounts/services';
import { MetatraderPswdPopupComponent } from '@personal-area/global/accounts/ui';
import { retryStrategy } from '@personal-area/global/http/services';
import { I18nService } from '@personal-area/global/i18n';
import { LoadingState } from '@personal-area/global/state';

interface PasswordComponentState {
  loadingState: LoadingState;
  password: string;
}

@Injectable()
export class PasswordComponentStore
  extends ComponentStore<PasswordComponentState>
  implements OnDestroy {
  public readonly loadingState$ = this.select((state) => state.loadingState);
  public readonly password$ = this.select((state) => state.password);

  public readonly getPassword = this.effect((account$: Observable<Account>) =>
    account$.pipe(
      tap(() => {
        this._unsubscribe$.next();
        this.setState({
          password: '',
          loadingState: LoadingState.loading,
        });
      }),
      switchMap((account) =>
        this._accountService
          .getCachedCertificate(account.userId, account.id)
          .pipe(
            catchError((error: Error) => of(error)),
            takeUntil(this._unsubscribe$),
          ),
      ),
      tap((certificate: AccountCertificate | Error) => {
        if (certificate instanceof Error) {
          if (certificate.message === AccountServiceError.NoCertificateLogin) {
            this.patchState({ loadingState: LoadingState.initial });
          } else {
            this.patchState({ loadingState: LoadingState.failed });
          }
        } else {
          this.setState({
            password: certificate.password,
            loadingState: LoadingState.loaded,
          });
        }
      }),
    ),
  );

  public readonly generatePassword = this.effect(
    (account$: Observable<Account>) =>
      account$.pipe(
        switchMap((account) =>
          this._dialog
            .open(MetatraderPswdPopupComponent, {
              panelClass: 'regular-popup-container',
              direction: this._i18nService.isRtl ? 'rtl' : 'ltr',
            })
            .afterClosed()
            .pipe(
              filter(Boolean),
              tap(() => {
                this.patchState({ loadingState: LoadingState.loading });
                this._accountsFacade.removeCertificate(account.id);
              }),
              switchMap(() =>
                this._accountService
                  .changeMetaTraderPswd(account.userId, account.id)
                  .pipe(
                    retryWhen(retryStrategy()),
                    takeUntil(this._unsubscribe$),
                  ),
              ),
              switchMap(() => this._accountSocketService.certificate),
              first(({ id }) => id === account.id),
            ),
        ),
        switchMap((certificate) =>
          this._accountService.extendCertWithServer(certificate),
        ),
        tapResponse({
    next: (certificate: AccountCertificate) => {
        this._accountsFacade.saveCertificates(certificate.id, certificate);
        this.setState({
            password: certificate.password,
            loadingState: LoadingState.loaded,
        });
    },
    error: () => this.patchState({ loadingState: LoadingState.failed })
}),
      ),
  );

  private _unsubscribe$ = new Subject<void>();
  private _accountSocketService = inject(AccountSocketService);

  constructor(
    private _accountService: AccountService,
    private _dialog: MatDialog,
    private _accountsFacade: AccountsFacade,
    private _i18nService: I18nService,
  ) {
    super({
      loadingState: LoadingState.loading,
      password: '',
    });
  }

  public override ngOnDestroy() {
    super.ngOnDestroy();
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
  }
}
